import type { Client } from '@urql/core'
import { opsClient } from 'app/lib/opsUrqlClient'
import { AuthStore } from './AuthStore'
import { ContactStore } from './ContactStore'
import { ActivityStore } from './ActivityStore'
import { TaskStore } from './TaskStore'
import { AppointmentStore } from './AppointmentStore'
import { PatientStore } from './PatientStore'
import { TransactionStore } from './TransactionStore'
import { MessageStore } from './MessageStore'
import { CallStore } from './CallStore'
import { UserStore } from './UserStore'
import { RoleStore } from './RoleStore'
import { WorkspaceStore } from './WorkspaceStore'
import { LocationStore } from './LocationStore'
import { FollowUpStore } from './FollowUpStore'
import { TagColorStore } from './TagColorStore'
import { PipelineStore } from './PipelineStore'
import { ListMembershipStore } from './ListMembershipStore'
import { SystemListStore } from './SystemListStore'
import { ListStore } from './ListStore'
import { NotificationStore } from './NotificationStore'
import { TreatmentPlanStore } from './TreatmentPlanStore'
import { ProcedureStore } from './ProcedureStore'
import { AdCampaignStore } from './AdCampaignStore'
import { AdPlatformMappingStore } from './AdPlatformMappingStore'
import { AIAgentConfigurationStore } from './AIAgentConfigurationStore'
import { AIAgentStore } from './AIAgentStore'
import { AppointmentTypeStore } from './AppointmentTypeStore'
import { BudgetStore } from './BudgetStore'
import { CdtCodeMappingStore } from './CdtCodeMappingStore'
import { ChannelMappingStore } from './ChannelMappingStore'
import { ConversionLogStore } from './ConversionLogStore'
import { ExtractionPromptStore } from './ExtractionPromptStore'
import { FeatureToggleStore } from './FeatureToggleStore'
import { FeatureToggleDefinitionStore } from './FeatureToggleDefinitionStore'
import { FormFieldMappingStore } from './FormFieldMappingStore'
import { UnmappedFormKeyStore } from './UnmappedFormKeyStore'
import { ImportStore } from './ImportStore'
import { IncomingCallStore } from './IncomingCallStore'
import { ProviderStore } from './ProviderStore'
import { ScorecardStore } from './ScorecardStore'
import { NoteTemplateStore } from './NoteTemplateStore'
import { SmsTemplateStore } from './SmsTemplateStore'
import { TaskTemplateStore } from './TaskTemplateStore'
import { UserSettingStore } from './UserSettingStore'
import { UtmAliasStore } from './UtmAliasStore'
import { CustomPageStore } from './CustomPageStore'
import { WorkspaceInviteStore } from './WorkspaceInviteStore'
import { SmsAgentOpsStore } from './SmsAgentOpsStore'
import { VoiceAgentOpsStore } from './VoiceAgentOpsStore'

// The subset of EntityStore the event router drives: a loaded check plus the two
// mutators it calls. Every registered store is an EntityStore subclass, so this is
// structurally satisfied without a cast.
type EventTargetStore = {
  list?: { hits: { id?: string }[] }
  current?: { id?: string }
  remove(id: string): unknown
  applyPatch(id: string, patch: Record<string, unknown>): void
}

export class RootStore {
  readonly auth: AuthStore
  readonly contacts: ContactStore
  readonly activities: ActivityStore
  readonly tasks: TaskStore
  readonly appointments: AppointmentStore
  readonly patients: PatientStore
  readonly transactions: TransactionStore
  readonly messages: MessageStore
  readonly calls: CallStore
  readonly users: UserStore
  readonly roles: RoleStore
  readonly workspaces: WorkspaceStore
  readonly locations: LocationStore
  readonly followUps: FollowUpStore
  readonly tagColors: TagColorStore
  readonly pipelines: PipelineStore
  readonly lists: ListStore
  readonly listMemberships: ListMembershipStore
  readonly systemLists: SystemListStore
  readonly notifications: NotificationStore
  readonly treatmentPlans: TreatmentPlanStore
  readonly procedures: ProcedureStore
  readonly adCampaigns: AdCampaignStore
  readonly adPlatformMappings: AdPlatformMappingStore
  readonly aiAgentConfigurations: AIAgentConfigurationStore
  readonly aiAgent: AIAgentStore
  readonly appointmentTypes: AppointmentTypeStore
  readonly budgets: BudgetStore
  readonly cdtCodeMappings: CdtCodeMappingStore
  readonly channelMappings: ChannelMappingStore
  readonly conversionLogs: ConversionLogStore
  readonly extractionPrompts: ExtractionPromptStore
  readonly featureToggles: FeatureToggleStore
  readonly featureToggleDefinitions: FeatureToggleDefinitionStore
  readonly formFieldMappings: FormFieldMappingStore
  readonly unmappedFormKeys: UnmappedFormKeyStore
  readonly imports: ImportStore
  readonly incomingCalls: IncomingCallStore
  readonly providers: ProviderStore
  readonly scorecards: ScorecardStore
  readonly smsTemplates: SmsTemplateStore
  readonly taskTemplates: TaskTemplateStore
  readonly noteTemplates: NoteTemplateStore
  readonly userSettings: UserSettingStore
  readonly utmAliases: UtmAliasStore
  readonly customPages: CustomPageStore
  readonly workspaceInvites: WorkspaceInviteStore
  readonly voiceAgentOps: VoiceAgentOpsStore
  readonly smsAgentOps: SmsAgentOpsStore

  constructor(client: Client) {
    this.auth = new AuthStore(client)
    this.contacts = new ContactStore(client)
    this.activities = new ActivityStore(client)
    this.tasks = new TaskStore(client)
    this.appointments = new AppointmentStore(client)
    this.patients = new PatientStore(client)
    this.transactions = new TransactionStore(client)
    this.messages = new MessageStore(client)
    this.calls = new CallStore(client)
    this.users = new UserStore(client)
    this.roles = new RoleStore(client)
    this.workspaces = new WorkspaceStore(client)
    this.locations = new LocationStore(client)
    this.followUps = new FollowUpStore(client)
    this.tagColors = new TagColorStore(client)
    this.pipelines = new PipelineStore(client)
    this.lists = new ListStore(client)
    this.listMemberships = new ListMembershipStore()
    this.systemLists = new SystemListStore()
    this.notifications = new NotificationStore(client)
    this.treatmentPlans = new TreatmentPlanStore(client)
    this.procedures = new ProcedureStore(client)
    this.adCampaigns = new AdCampaignStore(client)
    this.adPlatformMappings = new AdPlatformMappingStore(client)
    this.aiAgentConfigurations = new AIAgentConfigurationStore(opsClient)
    this.appointmentTypes = new AppointmentTypeStore(client)
    this.aiAgent = new AIAgentStore(this.locations, this.appointmentTypes)
    this.budgets = new BudgetStore(client)
    this.cdtCodeMappings = new CdtCodeMappingStore(client)
    this.channelMappings = new ChannelMappingStore(client)
    this.conversionLogs = new ConversionLogStore(client)
    this.extractionPrompts = new ExtractionPromptStore(client)
    this.featureToggles = new FeatureToggleStore(client)
    this.featureToggleDefinitions = new FeatureToggleDefinitionStore(client)
    this.formFieldMappings = new FormFieldMappingStore(client)
    this.unmappedFormKeys = new UnmappedFormKeyStore(client)
    this.imports = new ImportStore(client)
    this.incomingCalls = new IncomingCallStore(client)
    this.providers = new ProviderStore(client)
    this.scorecards = new ScorecardStore(client)
    this.smsTemplates = new SmsTemplateStore(client)
    this.taskTemplates = new TaskTemplateStore(client)
    this.noteTemplates = new NoteTemplateStore(client)
    this.userSettings = new UserSettingStore(client)
    this.utmAliases = new UtmAliasStore(client)
    this.customPages = new CustomPageStore(client)
    this.workspaceInvites = new WorkspaceInviteStore(client)
    this.voiceAgentOps = new VoiceAgentOpsStore()
    this.smsAgentOps = new SmsAgentOpsStore()

    this.eventTypeStores = {
      Contact: this.contacts,
      Call: this.calls,
      Activity: this.activities,
      Message: this.messages,
      Appointment: this.appointments,
      Task: this.tasks,
      TreatmentPlan: this.treatmentPlans,
    }
  }

  private readonly eventTypeStores: Record<string, EventTargetStore>

  storeForEventType(type: string): EventTargetStore | undefined {
    return this.eventTypeStores[type]
  }

  resetAll() {
    this.auth.reset()
    this.contacts.reset()
    this.activities.reset()
    this.tasks.reset()
    this.appointments.reset()
    this.patients.reset()
    this.transactions.reset()
    this.messages.reset()
    this.calls.reset()
    this.users.reset()
    this.roles.reset()
    this.workspaces.reset()
    this.locations.reset()
    this.followUps.reset()
    this.tagColors.reset()
    this.pipelines.reset()
    this.lists.reset()
    this.notifications.reset()
    this.treatmentPlans.reset()
    this.procedures.reset()
    this.adCampaigns.reset()
    this.adPlatformMappings.reset()
    this.aiAgentConfigurations.reset()
    this.aiAgent.reset()
    this.appointmentTypes.reset()
    this.budgets.reset()
    this.cdtCodeMappings.reset()
    this.channelMappings.reset()
    this.conversionLogs.reset()
    this.extractionPrompts.reset()
    this.featureToggles.reset()
    this.featureToggleDefinitions.reset()
    this.formFieldMappings.reset()
    this.unmappedFormKeys.reset()
    this.imports.reset()
    this.incomingCalls.reset()
    this.providers.reset()
    this.scorecards.reset()
    this.smsTemplates.reset()
    this.taskTemplates.reset()
    this.noteTemplates.reset()
    this.userSettings.reset()
    this.utmAliases.reset()
    this.customPages.reset()
    this.workspaceInvites.reset()
    this.voiceAgentOps.reset()
    this.smsAgentOps.reset()
  }
}
